import React, { useState } from "react";
import { TableCell, TableRow, Typography, Stack, Avatar, Box } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";

import TableCommon from "../../Common/Table";
import { handleDateTimeDefault } from "../../../helpers/utils";
import ActionMenu, { ActionTypes } from "../../Common/ActionMenu";
import ConfirmationDialog from "../../Common/ConfirmationDialog";
import { baseUrl } from "../../../helpers/constants";

const InsuranceTableComponent = ({
    loading,
    fetching = false,
    count,
    data,
    onViewDetails,
    onDelete,
    pagination,
    handlePagination,
}) => {
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [insuranceToDelete, setInsuranceToDelete] = useState(null);

    const handleDeleteClick = (row) => {
        setInsuranceToDelete(row);
        setConfirmationOpen(true);
    };

    const handleConfirmDelete = () => {
        if (insuranceToDelete && onDelete) {
            onDelete(insuranceToDelete);
        }
        setConfirmationOpen(false);
        setInsuranceToDelete(null);
    };

    const handleCloseConfirmation = () => {
        setConfirmationOpen(false);
        setInsuranceToDelete(null);
    };

    let columns = [
        {
            id: "insurance_number",
            title: "Insurance #",
            field: "insurance_number",
            minWidth: 140,
            sort: true,
        },
        {
            id: "customer",
            title: "Customer Details",
            minWidth: 200,
            sort: false,
        },
        {
            id: "product",
            title: "Product",
            minWidth: 150,
            sort: false,
        },
        {
            id: "policy_info",
            title: "Policy Info",
            minWidth: 180,
            sort: false,
        },
        {
            id: "payment_status",
            title: "Payment Status",
            field: "payment_status",
            minWidth: 120,
            sort: true,
            align: "center",
        },
        {
            id: "created_at",
            title: "Applied Date",
            field: "created_at",
            minWidth: 120,
            sort: true,
        },
        {
            id: "actions",
            title: "Actions",
            minWidth: 100,
            sort: false,
            align: "center",
        },
    ];

    const getPaymentStatusColor = (status) => {
        const statusColors = {
            pending: { bg: "#fff3cd", color: "#856404" },
            paid: { bg: "#d4edda", color: "#155724" },
            failed: { bg: "#f8d7da", color: "#721c24" },
            cancelled: { bg: "#f8f9fa", color: "#6c757d" },
        };
        return statusColors[status] || { bg: "#f8f9fa", color: "#6c757d" };
    };

    const renderTableData =
        data?.length > 0
            ? data.map((row, index) => {
                  const statusColor = getPaymentStatusColor(row.payment_status);
                  return (
                      <TableRow hover key={row.id} tabIndex={-1}>
                          <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                  {row.insurance_number}
                              </Typography>
                          </TableCell>
                          <TableCell>
                              <Stack spacing={0.5}>
                                  <Typography variant="body2" fontWeight="medium">
                                      {row.customer_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                      {row.mobile} • {row.email}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                      {row.city}, {row.state}
                                  </Typography>
                              </Stack>
                          </TableCell>
                          <TableCell>
                              <Stack spacing={0.5}>
                                  <Box display="flex" alignItems="center" gap={1}>
                                      {row.product?.image_url && (
                                          <Avatar
                                              src={`${baseUrl}${row.product.image_url}`}
                                              alt={row.product.name}
                                              sx={{ width: 24, height: 24 }}
                                          />
                                      )}
                                      <Typography variant="body2" fontWeight="medium" noWrap>
                                          {row.product?.name || "N/A"}
                                      </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                      {row.product?.description || "No description"}
                                  </Typography>
                              </Stack>
                          </TableCell>
                          <TableCell>
                              <Stack spacing={0.5}>
                                  <Typography variant="body2" color="text.secondary">
                                      Policy: {row.policy_number}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                      Expiry: {row.expiry_date ? new Date(row.expiry_date).toLocaleDateString() : "N/A"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                      Premium: ₹{row.premium_amount || 0}
                                  </Typography>
                                  {(() => {
                                      let filesCount = 0;
                                      try {
                                          if (typeof row.files === "string") {
                                              const parsedFiles = JSON.parse(row.files);
                                              filesCount = Array.isArray(parsedFiles) ? parsedFiles.length : 0;
                                          } else if (Array.isArray(row.files)) {
                                              filesCount = row.files.length;
                                          }
                                      } catch (error) {
                                          filesCount = 0;
                                      }
                                      return (
                                          filesCount > 0 && (
                                              <Typography variant="caption" color="text.secondary">
                                                  {filesCount} file(s)
                                              </Typography>
                                          )
                                      );
                                  })()}
                              </Stack>
                          </TableCell>
                          <TableCell align="center">
                              <Typography
                                  variant="body2"
                                  sx={{
                                      backgroundColor: statusColor.bg,
                                      color: statusColor.color,
                                      fontWeight: "medium",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      fontSize: "0.75rem",
                                  }}
                              >
                                  {row.payment_status?.toUpperCase() || "PENDING"}
                              </Typography>
                          </TableCell>
                          <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                  {handleDateTimeDefault(row.created_at)}
                              </Typography>
                          </TableCell>
                          <TableCell align="center">
                              <ActionMenu
                                  actions={[
                                      {
                                          ...ActionTypes.VIEW,
                                          onClick: () => onViewDetails(row),
                                      },
                                      {
                                          ...ActionTypes.DELETE,
                                          onClick: () => handleDeleteClick(row),
                                      },
                                  ]}
                                  row={row}
                                  showStatusToggle={false}
                              />
                          </TableCell>
                      </TableRow>
                  );
              })
            : null;

    return (
        <>
            <TableCommon
                columns={columns}
                tableData={renderTableData}
                loading={loading}
                fetching={fetching}
                count={count}
                pagination={pagination}
                handlePagination={handlePagination}
            />
            <ConfirmationDialog
                open={confirmationOpen}
                title="Are you sure you want to delete this insurance renewal?"
                confirmButtonTitle="Yes, Delete"
                onConfirm={handleConfirmDelete}
                onClose={handleCloseConfirmation}
            />
        </>
    );
};

export default InsuranceTableComponent;
